import uniqueId from 'lodash/uniqueId'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { useTheme } from '@opallabs/hooks'
import { InputLabel } from '../InputLabel'
import { TextInput } from '../TextInput'
import * as styles from './styles'
import type { ImperativeHandle, TextInputWithLabelProps } from './types'

export { selectors } from './styles'
export type {
  ImperativeHandle as TextInputImperativeHandle,
  TextInputWithLabelProps,
}

export const TextInputWithLabel = forwardRef<
  ImperativeHandle,
  TextInputWithLabelProps
>(
  (
    {
      autocompleteHint,
      clearable,
      controlsHtmlId,
      disabled = false,
      explicitError = '',
      forceValidation = false,
      initialValue,
      labelText,
      leadingIconGlyph,
      maxLength,
      maxValue,
      minLength,
      minValue,
      onChange,
      onContainerMouseOut,
      onContainerMouseOver,
      onInputBlur,
      onInputFocus,
      onKeyDown,
      onValidate,
      passwordCharacterPatterns,
      placeholder,
      previouslyModified = false,
      required = false,
      styleOverrides = {},
      theme: propTheme,
      type,
      value,
      validator,
      ...otherTextInputProps
    },
    externalRef
  ) => {
    const [modified, setModified] = useState<boolean>(previouslyModified)
    const [valid, setValid] = useState<boolean>(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const providedTheme = useTheme()
    const theme = propTheme ?? providedTheme

    /**
     * allow parents passing in a `ref` to call methods against `inputRef`
     * ex:
     *   const myRef = useRef<TextInputImperitiveHandle>(null);
     *   <TextInputWithLabel ref={myRef} />
     *
     *   myRef.current?.focus(); // focuses inputRef from anywhere in parent component
     */
    useImperativeHandle(
      externalRef,
      () => ({
        forceValidation() {
          inputRef.current?.dispatchEvent(
            new Event('change', { bubbles: true })
          )
          inputRef.current?.checkValidity()
        },
        focus() {
          inputRef.current?.focus()
        },
      }),
      [inputRef]
    )

    const inputId = uniqueId('lapidary-text-input-with-label')

    return (
      <div
        className={styles.selectors.container}
        css={[styles.container, styleOverrides?.container]}
      >
        <InputLabel
          disabled={disabled}
          inputId={inputId}
          modified={modified}
          required={required}
          styleOverrides={{
            ...styles.label,
            ...styleOverrides?.label,
          }}
          text={labelText}
          theme={theme}
          valid={valid && explicitError === ''}
        />
        <TextInput
          autocompleteHint={autocompleteHint}
          clearable={clearable}
          controlsHtmlId={controlsHtmlId}
          disabled={disabled}
          explicitError={explicitError}
          forceValidation={forceValidation}
          htmlId={inputId}
          initialValue={initialValue}
          leadingIconGlyph={leadingIconGlyph}
          maxLength={maxLength}
          maxValue={maxValue}
          minLength={minLength}
          minValue={minValue}
          onChange={(newValue: string): void => {
            onChange(newValue)
            setModified(true)
          }}
          onContainerMouseOut={onContainerMouseOut}
          onContainerMouseOver={onContainerMouseOver}
          onInputBlur={onInputBlur}
          onInputFocus={onInputFocus}
          onKeyDown={onKeyDown}
          onValidate={(newValid: boolean): void => {
            setValid(newValid)

            if (onValidate) onValidate(newValid)
          }}
          passwordCharacterPatterns={passwordCharacterPatterns}
          placeholder={placeholder}
          previouslyModified={modified}
          ref={inputRef}
          required={required}
          styleOverrides={{
            container: styleOverrides?.inputContainer,
            input: styleOverrides?.input,
          }}
          theme={theme}
          type={type}
          value={value}
          validator={validator}
          {...otherTextInputProps}
        />
      </div>
    )
  }
)
TextInputWithLabel.displayName = 'Lapidary: TextInputWithLabel'
